import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import '../styles/Home.css';
import '../styles/EliteEffects.css';

const ProductCard = ({ product, onAddToCart }) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleAddToCart = () => {
    onAddToCart(product);
    toast.success(`${product.name} dodano do koszyka`);
  };

  // Desktop hover effect
  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current || window.innerWidth < 768) return;
    
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      const card = cardRef.current;
      if (!card) return;
      
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = ((y - centerY) / centerY) * -8;
      const rotateY = ((x - centerX) / centerX) * 8;
      
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05) translateZ(50px)`;
      
      const shadowX = ((x - centerX) / centerX) * 15;
      const shadowY = ((y - centerY) / centerY) * 15;
      card.style.boxShadow = `${shadowX}px ${shadowY}px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(255, 255, 255, 0.1)`;
    });
  }, []);

  // Mobile touch tilt effect - MORE VISIBLE
  const handleTouchMove = useCallback((e) => {
    if (!cardRef.current || !isMobile) return;
    
    const touch = e.touches[0];
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // MORE DRAMATIC tilt on mobile for visibility
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.03) translateZ(40px)`;
    card.style.boxShadow = `
      0 20px 50px rgba(0, 0, 0, 0.35),
      0 8px 20px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(255, 255, 255, 0.1)
    `;
  }, [isMobile]);

  const handleTouchEnd = useCallback(() => {
    if (!cardRef.current || !isMobile) return;
    const card = cardRef.current;
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1) translateZ(0)';
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!cardRef.current) return;
    
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    const card = cardRef.current;
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1) translateZ(0)';
    card.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
    setIsHovered(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  return (
    <div 
      ref={cardRef}
      className="product-card hover-lift blur-reveal dynamic-shadow mobile-tilt-active"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="chrome-reflection"></div>
      <div className="product-image-wrapper">
        <img 
          src={product.image} 
          alt={product.name}
          className="product-image"
          loading="lazy"
        />
      </div>
      <div className="product-info">
        <h3 className="product-name heading-3">{product.name}</h3>
        <p className="product-description body-small">{product.description}</p>
        <div className="product-footer">
          <span className="product-price glow-breathe">{product.price.toFixed(2)} zł</span>
          <button 
            className="add-to-cart-btn ultra-smooth"
            onClick={handleAddToCart}
            aria-label={`Dodaj ${product.name} do koszyka`}
          >
            <Plus size={18} />
            Dodaj do koszyka
          </button>
        </div>
      </div>
    </div>
  );
};

const Products = ({ products, onAddToCart }) => {
  React.useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          if (isMobile) {
            entry.target.classList.add('revealed');
          }
        }
      });
    }, observerOptions);

    const cards = document.querySelectorAll('.product-card');
    cards.forEach((card, index) => {
      card.style.transitionDelay = `${index * 0.1}s`;
      if (isMobile) {
        card.classList.add('mobile-scroll-reveal');
      }
      observer.observe(card);
    });

    // Mobile parallax scroll effect
    if (isMobile) {
      let ticking = false;
      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const scrolled = window.scrollY;
            const cards = document.querySelectorAll('.product-card');
            cards.forEach((card, index) => {
              const rect = card.getBoundingClientRect();
              if (rect.top < window.innerHeight && rect.bottom > 0) {
                // Subtle parallax based on scroll position
                const offset = (window.innerHeight - rect.top) * 0.03;
                card.style.transform = `translateY(${-offset}px)`;
              }
            });
            ticking = false;
          });
          ticking = true;
        }
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', handleScroll);
      };
    }

    return () => observer.disconnect();
  }, [products]);

  return (
    <section id="products" className="products section-padding">
      <div className="container">
        <div className="section-header animate-on-scroll mobile-float">
          <h2 className="heading-1">Nasze produkty</h2>
          <p className="body-regular">Wyselekcjonowana kolekcja najlepszych produktów</p>
        </div>
        <div className="products-grid">
          {products.map(product => (
            <ProductCard 
              key={product.id} 
              product={product}
              onAddToCart={onAddToCart}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
